import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Eye, EyeOff, Loader2, FileText, CheckCircle2, ArrowLeft, Mail, ShieldCheck, Clock, XCircle, Building2, Landmark } from 'lucide-react';
import LandingNavBar from '../../components/LandingNavBar';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const portalApi = axios.create({ baseURL: API_URL });

const SUBSCRIPTION_PLANS = [
  { id: 'starter', name: 'Starter', price: 'RM 49/month', desc: '500 invoices/month, 2 PMS integrations' },
  { id: 'professional', name: 'Professional', price: 'RM 99/month', desc: '1,500 invoices/month, Unlimited PMS', popular: true },
  { id: 'business', name: 'Business', price: 'RM 199/month', desc: '5,000 invoices/month, Multi-hotel support' },
  { id: 'enterprise', name: 'Enterprise', price: 'Custom', desc: 'Unlimited invoices, Dedicated support' },
];

function parseApiError(err) {
  const data = err.response?.data;
  if (!data) return err.message || 'Network error';
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) return data.detail.map(d => d.msg || String(d)).join(', ');
  return err.message || 'Request failed';
}

export default function PortalAuth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'signup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 2FA state
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiryMinutes, setExpiryMinutes] = useState(5);
  const codeInputRefs = useRef([]);

  // Pending approval state
  const [showPendingApproval, setShowPendingApproval] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');

  // Multi-step signup
  const [signupStep, setSignupStep] = useState(1);

  const [formData, setFormData] = useState({
    email: '', password: '', full_name: '', phone: '', subscription_plan: '',
    company_name: '', tin: '', brn: '', sst_number: '', ttx_number: '',
    msic_code: '55101', bank_name: '', bank_account_number: ''
  });

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    setError('');
    // Validate Step 1 fields
    if (!formData.email?.trim()) { setError('Email is required'); return; }
    if (!formData.password || formData.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!formData.company_name?.trim()) { setError('Company name is required'); return; }
    setSignupStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isLogin) {
        const { data } = await portalApi.post('/api/einvoice/portal/auth/login', formData);
        if (data.requires_verification) {
          setVerificationEmail(data.email);
          setMaskedEmail(data.masked_email || data.email);
          setExpiryMinutes(data.expires_in_minutes || 5);
          setShowVerification(true);
          setResendCooldown(60);
          setVerificationCode(['', '', '', '', '', '']);
        } else if (data.token) {
          handleLoginSuccess(data);
        }
      } else {
        // Registration — require subscription plan
        if (!formData.subscription_plan) {
          setError('Please select a subscription plan');
          setLoading(false);
          return;
        }
        if (!formData.tin?.trim() || !formData.brn?.trim()) {
          setError('TIN and BRN are required');
          setLoading(false);
          return;
        }
        const { data } = await portalApi.post('/api/einvoice/portal/auth/register', formData);
        if (data.pending_approval) {
          setPendingMessage(data.message);
          setShowPendingApproval(true);
        } else {
          setIsLogin(true);
          setFormData({ ...formData, password: '' });
          setError('');
          alert('Registration successful! Please login to continue.');
        }
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 403) {
        setPendingMessage(detail);
        setShowPendingApproval(true);
      } else {
        setError(parseApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (data) => {
    localStorage.setItem('einvoice_token', data.token);
    localStorage.setItem('einvoice_user', JSON.stringify(data.user));
    navigate(data.user.onboarding_completed ? '/einvoice/portal/dashboard' : '/einvoice/portal/onboarding');
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError('');

    // Auto-advance to next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newCode.every(d => d !== '')) {
      handleVerifyCode(newCode.join(''));
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const digits = pasted.split('');
      setVerificationCode(digits);
      codeInputRefs.current[5]?.focus();
      handleVerifyCode(pasted);
    }
  };

  const handleVerifyCode = async (code) => {
    if (!code) code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const { data } = await portalApi.post('/api/einvoice/portal/auth/verify-code', {
        email: verificationEmail,
        code: code
      });
      if (data.token) {
        handleLoginSuccess(data);
      }
    } catch (err) {
      setError(parseApiError(err));
      setVerificationCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    setError('');
    try {
      await portalApi.post('/api/einvoice/portal/auth/resend-code', {
        email: verificationEmail
      });
      setResendCooldown(60);
      setVerificationCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
      setError('');
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowVerification(false);
    setShowPendingApproval(false);
    setVerificationCode(['', '', '', '', '', '']);
    setError('');
    setPendingMessage('');
  };

  // ==================== RENDER ====================

  const renderPendingApprovalScreen = () => (
    <Card className="bg-white/10 backdrop-blur-lg border-white/20" data-testid="pending-approval-card">
      <CardHeader className="text-center pb-2">
        <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
          <Clock className="w-8 h-8 text-amber-400" />
        </div>
        <CardTitle className="text-white text-2xl">
          {pendingMessage?.includes('declined') ? 'Registration Declined' : 'Pending Approval'}
        </CardTitle>
        <CardDescription className="text-slate-300 mt-3 leading-relaxed">
          {pendingMessage || 'Your account is pending admin approval. You will receive an email once approved.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {pendingMessage?.includes('declined') ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
            <XCircle className="w-5 h-5 text-red-400 mx-auto mb-2" />
            <p className="text-red-200 text-sm">Contact <span className="text-red-300 font-medium">jedeelooi@gmail.com</span> for assistance.</p>
          </div>
        ) : (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
            <Mail className="w-5 h-5 text-amber-400 mx-auto mb-2" />
            <p className="text-amber-200 text-sm">You'll receive an email notification once your account is reviewed.</p>
          </div>
        )}
        <button
          type="button"
          onClick={handleBackToLogin}
          className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mt-4"
          data-testid="back-to-login-from-pending-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>
      </CardContent>
    </Card>
  );

  const renderVerificationScreen = () => (
    <Card className="bg-white/10 backdrop-blur-lg border-white/20" data-testid="otp-verification-card">
      <CardHeader className="text-center pb-2">
        <div className="mx-auto w-16 h-16 bg-[#2563EB]/20 rounded-full flex items-center justify-center mb-4">
          <ShieldCheck className="w-8 h-8 text-[#60A5FA]" />
        </div>
        <CardTitle className="text-white text-2xl">Verify Your Identity</CardTitle>
        <CardDescription className="text-slate-300 mt-2">
          We've sent a 6-digit verification code to<br />
          <span className="text-[#60A5FA] font-medium">{maskedEmail}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive" className="bg-red-500/20 border-red-500/50">
            <AlertDescription className="text-red-200">{error}</AlertDescription>
          </Alert>
        )}

        {/* 6-digit code inputs */}
        <div className="flex justify-center gap-2" data-testid="otp-input-group">
          {verificationCode.map((digit, i) => (
            <input
              key={i}
              ref={el => codeInputRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleCodeChange(i, e.target.value)}
              onKeyDown={e => handleCodeKeyDown(i, e)}
              onPaste={i === 0 ? handleCodePaste : undefined}
              className="w-12 h-14 text-center text-2xl font-bold bg-white/10 border border-white/30 rounded-lg text-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/50 outline-none transition-all"
              data-testid={`otp-input-${i}`}
              autoFocus={i === 0}
            />
          ))}
        </div>

        <p className="text-center text-sm text-slate-400">
          Code expires in <span className="text-[#60A5FA] font-medium">{expiryMinutes} minutes</span>
        </p>

        <Button
          onClick={() => handleVerifyCode()}
          disabled={loading || verificationCode.some(d => !d)}
          className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11"
          data-testid="verify-otp-btn"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</>
          ) : (
            'Verify & Sign In'
          )}
        </Button>

        {/* Resend */}
        <div className="text-center space-y-2">
          <p className="text-sm text-slate-400">Didn't receive the code?</p>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={resendCooldown > 0 || loading}
            className={`text-sm font-medium transition-colors ${
              resendCooldown > 0
                ? 'text-slate-500 cursor-not-allowed'
                : 'text-[#60A5FA] hover:text-blue-300 cursor-pointer'
            }`}
            data-testid="resend-otp-btn"
          >
            {resendCooldown > 0
              ? `Resend code in ${resendCooldown}s`
              : 'Resend Code'}
          </button>
        </div>

        {/* Back to login */}
        <button
          type="button"
          onClick={handleBackToLogin}
          className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mt-2"
          data-testid="back-to-login-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>
      </CardContent>
    </Card>
  );

  const inputCls = "bg-white/10 border-white/20 text-white";
  const helperCls = "text-xs text-slate-500 mt-1";

  const renderLoginForm = () => {
    // LOGIN FORM
    if (isLogin) return (
      <Card className="bg-white/10 backdrop-blur-lg border-white/20">
        <CardHeader className="text-center">
          <CardTitle className="text-white text-2xl">Welcome Back</CardTitle>
          <CardDescription className="text-slate-300">Sign in to manage your e-invoices</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive" className="bg-red-500/20 border-red-500/50">
                <AlertDescription className="text-red-200">{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-200">Email</Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required
                className={inputCls} data-testid="auth-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-200">Password</Label>
              <div className="relative">
                <Input id="password" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange}
                  required minLength={8} className={`${inputCls} pr-10`} data-testid="auth-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11" data-testid="auth-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</> : 'Sign In'}
            </Button>
            <div className="text-center pt-4">
              <button type="button" onClick={() => { setIsLogin(false); setError(''); setSignupStep(1); }} className="text-[#60A5FA] hover:text-blue-300 text-sm" data-testid="auth-toggle-btn">
                Don't have an account? Sign up
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    );

    // SIGNUP STEP 1 — Minimal fields
    if (signupStep === 1) return (
      <Card className="bg-white/10 backdrop-blur-lg border-white/20" data-testid="signup-step-1">
        <CardHeader className="text-center">
          <CardTitle className="text-white text-2xl">Get Started</CardTitle>
          <CardDescription className="text-slate-300">Create your e-Invoice account in minutes</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleNextStep} className="space-y-4">
            {error && (
              <Alert variant="destructive" className="bg-red-500/20 border-red-500/50">
                <AlertDescription className="text-red-200">{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-slate-200">Email <span className="text-red-400">*</span></Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required
                className={inputCls} data-testid="auth-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-slate-200">Password <span className="text-red-400">*</span></Label>
              <div className="relative">
                <Input id="password" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange}
                  required minLength={8} className={`${inputCls} pr-10`} data-testid="auth-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className={helperCls}>Minimum 8 characters</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company_name" className="text-slate-200">Company Name <span className="text-red-400">*</span></Label>
              <Input id="company_name" name="company_name" value={formData.company_name} onChange={handleChange} required
                className={inputCls} data-testid="register-company-name-input" />
              <p className={helperCls}>Use your SSM registered company name</p>
            </div>

            <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 text-base font-semibold" data-testid="signup-step1-btn">
              Start Free Setup
            </Button>
            <p className="text-center text-xs text-slate-500" data-testid="no-credit-card-text">No credit card required</p>

            <div className="text-center pt-2">
              <button type="button" onClick={() => { setIsLogin(true); setError(''); setSignupStep(1); }} className="text-[#60A5FA] hover:text-blue-300 text-sm" data-testid="auth-toggle-btn">
                Already have an account? Sign in
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    );

    // SIGNUP STEP 2 — Company details + Plan
    return (
      <Card className="bg-white/10 backdrop-blur-lg border-white/20 max-h-[80vh] overflow-y-auto" data-testid="signup-step-2">
        <CardHeader className="text-center sticky top-0 bg-[#1a2744]/95 backdrop-blur-md z-10 rounded-t-lg pb-3">
          <div className="flex items-center gap-3 mb-2">
            <button type="button" onClick={() => { setSignupStep(1); setError(''); }} className="text-slate-400 hover:text-white transition-colors" data-testid="step2-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs font-bold flex items-center justify-center">1</div>
                <div className="w-8 h-px bg-[#2563EB]" />
                <div className="w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs font-bold flex items-center justify-center">2</div>
              </div>
            </div>
          </div>
          <CardTitle className="text-white text-xl">Company & Plan Details</CardTitle>
          <CardDescription className="text-slate-400 text-sm">Complete your registration for <span className="text-[#60A5FA] font-medium">{formData.company_name}</span></CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive" className="bg-red-500/20 border-red-500/50">
                <AlertDescription className="text-red-200">{error}</AlertDescription>
              </Alert>
            )}

            {/* Contact info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="full_name" className="text-slate-200">Full Name</Label>
                <Input id="full_name" name="full_name" value={formData.full_name} onChange={handleChange}
                  className={inputCls} data-testid="register-fullname-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-slate-200">Phone</Label>
                <Input id="phone" name="phone" value={formData.phone} onChange={handleChange}
                  className={inputCls} data-testid="register-phone-input" />
              </div>
            </div>

            {/* Tax Details */}
            <div className="space-y-3 pt-1" data-testid="company-details-section">
              <div className="flex items-center gap-2 pb-1 border-b border-white/10">
                <Building2 className="w-4 h-4 text-[#60A5FA]" />
                <span className="text-slate-200 font-medium text-sm">Tax Registration</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="tin" className="text-slate-200">TIN <span className="text-red-400">*</span></Label>
                  <Input id="tin" name="tin" value={formData.tin} onChange={handleChange} required
                    className={inputCls} data-testid="register-tin-input" />
                  <p className={helperCls}>As per LHDN registration</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="brn" className="text-slate-200">BRN <span className="text-red-400">*</span></Label>
                  <Input id="brn" name="brn" value={formData.brn} onChange={handleChange} required
                    className={inputCls} data-testid="register-brn-input" />
                  <p className={helperCls}>SSM Business Registration Number</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="sst_number" className="text-slate-200">SST No</Label>
                  <Input id="sst_number" name="sst_number" value={formData.sst_number} onChange={handleChange}
                    className={inputCls} data-testid="register-sst-input" />
                  <p className={helperCls}>Leave blank if not applicable</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ttx_number" className="text-slate-200">Tourism Tax No</Label>
                  <Input id="ttx_number" name="ttx_number" value={formData.ttx_number} onChange={handleChange}
                    className={inputCls} data-testid="register-ttx-input" />
                  <p className={helperCls}>Only for registered operators</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="msic_code" className="text-slate-200">MSIC Code</Label>
                <Input id="msic_code" name="msic_code" value={formData.msic_code} onChange={handleChange}
                  className={inputCls} data-testid="register-msic-input" />
                <p className={helperCls}>Default: 55101 (Hotels and similar accommodation)</p>
              </div>
            </div>

            {/* Bank Details */}
            <div className="space-y-3 pt-1">
              <div className="flex items-center gap-2 pb-1 border-b border-white/10">
                <Landmark className="w-4 h-4 text-[#60A5FA]" />
                <span className="text-slate-200 font-medium text-sm">Bank Details</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="bank_name" className="text-slate-200">Bank Name</Label>
                  <Input id="bank_name" name="bank_name" value={formData.bank_name} onChange={handleChange}
                    className={inputCls} data-testid="register-bank-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bank_account_number" className="text-slate-200">Account No.</Label>
                  <Input id="bank_account_number" name="bank_account_number" value={formData.bank_account_number} onChange={handleChange}
                    className={inputCls} data-testid="register-bank-account-input" />
                </div>
              </div>
            </div>

            {/* Subscription Plan */}
            <div className="space-y-3 pt-1" data-testid="subscription-plan-section">
              <Label className="text-slate-200">Subscription Plan <span className="text-red-400">*</span></Label>
              <div className="grid grid-cols-1 gap-2">
                {SUBSCRIPTION_PLANS.map(plan => (
                  <label
                    key={plan.id}
                    className={`relative flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.subscription_plan === plan.id
                        ? 'bg-[#2563EB]/20 border-[#2563EB] ring-1 ring-[#2563EB]/50'
                        : 'bg-white/5 border-white/15 hover:border-white/30'
                    }`}
                    data-testid={`plan-option-${plan.id}`}
                  >
                    <input type="radio" name="subscription_plan" value={plan.id}
                      checked={formData.subscription_plan === plan.id} onChange={handleChange} className="sr-only" />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      formData.subscription_plan === plan.id ? 'border-[#2563EB] bg-[#2563EB]' : 'border-white/40'
                    }`}>
                      {formData.subscription_plan === plan.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-sm">{plan.name}</span>
                        {plan.popular && <span className="text-[10px] font-bold bg-[#2563EB] text-white px-1.5 py-0.5 rounded-full uppercase">Popular</span>}
                        <span className="text-[#60A5FA] font-semibold text-sm ml-auto">{plan.price}</span>
                      </div>
                      <p className="text-slate-400 text-xs mt-0.5">{plan.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 text-base font-semibold" data-testid="auth-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating account...</> : 'Start Issuing e-Invoices Today'}
            </Button>
            <p className="text-center text-xs text-slate-500" data-testid="no-credit-card-text-step2">No credit card required</p>

            <div className="text-center pt-2">
              <button type="button" onClick={() => { setIsLogin(true); setError(''); setSignupStep(1); setShowPendingApproval(false); }} className="text-[#60A5FA] hover:text-blue-300 text-sm" data-testid="auth-toggle-btn">
                Already have an account? Sign in
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="portal-auth-page">
      <LandingNavBar />

      <section className="flex-1 flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />

        <div className="relative w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left — Branding */}
            <div className="text-white space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#2563EB] rounded-xl flex items-center justify-center">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Staymii</h1>
                  <p className="text-slate-400 text-sm">e-Invoice Portal</p>
                </div>
              </div>

              <h2 className="text-3xl lg:text-4xl font-bold leading-tight">
                Malaysia's Universal<br />
                <span className="text-[#60A5FA]">Hotel e-Invoice Platform</span>
              </h2>

              <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                Connect your PMS, automate LHDN e-invoice compliance, and focus on hospitality.
              </p>

              <div className="space-y-4 pt-4">
                {['LHDN MyInvois Compliant', 'Connect any PMS or Channel Manager', 'Automatic invoice generation', 'Setup in under 5 minutes'].map(t => (
                  <div key={t} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle2 className="w-5 h-5 text-[#60A5FA] shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Auth Form or OTP Verification or Pending Approval */}
            {showPendingApproval ? renderPendingApprovalScreen() : showVerification ? renderVerificationScreen() : renderLoginForm()}
          </div>
        </div>
      </section>

      <footer className="py-6 text-center" style={{ backgroundColor: '#192336' }} data-testid="portal-auth-footer">
        <div className="flex items-center justify-center gap-4 mb-4">
          <Link to="/terms-of-service" className="text-white/70 text-sm hover:text-white transition-colors">Terms of Service</Link>
          <span className="text-white/50">|</span>
          <Link to="/privacy-policy" className="text-white/70 text-sm hover:text-white transition-colors">Privacy Policy</Link>
          <span className="text-white/50">|</span>
          <Link to="/contact" className="text-white/70 text-sm hover:text-white transition-colors">Contact Us</Link>
        </div>
        <p className="text-sm text-gray-400">Powered by Mii Global | StayMii Platform</p>
      </footer>
    </div>
  );
}
